import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import { CARRIERS } from '../data/carriers';
import { IMAGES } from '../Assets/Images/index';
import './ContainerPrefixPage.css';

// Build a flat list of all prefix codes mapped to their carrier
function buildPrefixList() {
  const prefixMap = {};
  CARRIERS.forEach((carrier) => {
    carrier.prefixCodes.forEach((code) => {
      // First carrier to claim a prefix wins (avoids duplicates from leasing overlap)
      if (!prefixMap[code]) {
        prefixMap[code] = {
          code,
          carrierName: carrier.name,
          carrierSlug: carrier.slug,
          logoKey: carrier.logoKey,
        };
      }
    });
  });
  return Object.values(prefixMap).sort((a, b) => a.code.localeCompare(b.code));
}

const ContainerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="13" rx="2" ry="2"/>
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="16"/>
    <line x1="8" y1="12" x2="8" y2="16"/>
    <line x1="16" y1="12" x2="16" y2="16"/>
  </svg>
);

function ContainerPrefixPage() {
  const allPrefixes = buildPrefixList();
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? allPrefixes.filter(
        (p) =>
          p.code.toLowerCase().includes(search.toLowerCase()) ||
          p.carrierName.toLowerCase().includes(search.toLowerCase())
      )
    : allPrefixes;

  return (
    <div className="prefix-page">
      <SEO
        title="Container Prefix List — All Shipping Container Codes"
        description="Browse the complete list of ISO container prefix codes used in global shipping. Each 4-letter prefix identifies the container owner or shipping line."
        canonicalPath="/container-prefixes"
      />
      <Header />

      {/* ── HERO ── */}
      <section className="prefix-hero">
        <div className="prefix-hero-inner">
          <nav className="prefix-breadcrumb">
            <Link to="/">Home</Link> / <Link to="/carriers">Ocean</Link> / <span>Container Prefixes</span>
          </nav>
          <h1 className="prefix-hero-title">Container Prefixes</h1>
          <p className="prefix-hero-desc">
            Container prefixes used in shipping — understand the 4-letter codes that identify container owners and ensure organisation in global container transportation.
          </p>
          <div className="prefix-search-wrap">
            <input
              type="text"
              className="prefix-search-input"
              placeholder="Search prefix or carrier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* ── GRID ── */}
      <section className="prefix-section">
        <div className="prefix-section-inner">
          {filtered.length === 0 ? (
            <p className="prefix-no-results">No prefixes found for "{search}".</p>
          ) : (
            <div className="prefix-grid">
              {filtered.map((item) => {
                const logo = item.logoKey ? IMAGES[item.logoKey] : null;
                const card = (
                  <div className={`prefix-card ${item.carrierSlug !== 'leasing' && item.logoKey ? 'prefix-card--linked' : ''}`}>
                    <div className="prefix-card-left">
                      <span className="prefix-card-icon">
                        <ContainerIcon />
                      </span>
                      <span className="prefix-card-code">{item.code}</span>
                    </div>
                    {logo && (
                      <img
                        src={logo}
                        alt={`${item.carrierName} logo`}
                        className="prefix-card-logo"
                      />
                    )}
                  </div>
                );

                // Link to carrier page only for named carriers with a logo
                if (item.logoKey && item.carrierSlug !== 'leasing') {
                  return (
                    <Link
                      key={item.code}
                      to={`/carriers/${item.carrierSlug}`}
                      className="prefix-card-link"
                      title={`${item.code} — ${item.carrierName}`}
                    >
                      {card}
                    </Link>
                  );
                }

                return (
                  <div key={item.code} className="prefix-card-link prefix-card-link--plain" title={item.carrierName}>
                    {card}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── INFO BLOCK ── */}
      <section className="prefix-info">
        <div className="prefix-info-inner">
          <h2>What Is a Container Prefix?</h2>
          <p>
            A container prefix (also called a container owner code) is a 3-letter alphabetic code followed by a 4th letter "U" that identifies the owner of a shipping container. The full container number format is: <strong>XXXX 000000 C</strong> — where <em>XXXX</em> is the owner prefix, <em>000000</em> is the serial number, and <em>C</em> is the check digit.
          </p>
          <p>
            These codes are standardised under ISO 6346 and are required on all containers used in international trade. Carrier-owned containers carry the shipping line's prefix, while leased containers carry the leasing company's prefix.
          </p>
          <p>
            Click on any prefix card above to visit the carrier's dedicated tracking page, where you can track containers, view SCAC codes, and learn more about each shipping line.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ContainerPrefixPage;
